import { sendMagicLink } from '../vote-intent-api';
import { isValidEmail } from '../utils/validation';
import { resolveTurnstileToken } from '../utils/turnstile-token';
import type { VoteIntentModal } from '../vote-intent-view';

export interface SubmitMagicLinkOptions {
  readonly email: string;
  readonly charityId: string;
  readonly periodId: string;
  readonly triggerButton: HTMLButtonElement;
  readonly statusHandler: (message: string) => void;
  readonly onSuccess: (modal: VoteIntentModal, email: string) => void;
}

function parseOptionalPostId(value: string): number | undefined {
  const trimmed = String(value).trim();
  if (trimmed === '') {
    return undefined;
  }

  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) || parsed <= 0 ? undefined : parsed;
}

export async function submitMagicLink(modal: VoteIntentModal, options: SubmitMagicLinkOptions): Promise<void> {
  const normalized = String(options.email).trim().toLowerCase();
  if (!isValidEmail(normalized)) {
    options.statusHandler('Please enter a valid email address.');
    return;
  }

  const turnstileToken = resolveTurnstileToken(modal.base.turnstileContainer);
  if (modal.base.turnstileContainer && !turnstileToken) {
    options.statusHandler('Please complete the security check.');
    return;
  }

  options.triggerButton.disabled = true;
  options.statusHandler('Sending sign-in link…');

  try {
    await sendMagicLink({
      email: normalized,
      charity_id: parseOptionalPostId(options.charityId),
      period_id: parseOptionalPostId(options.periodId),
      turnstile_token: turnstileToken ?? undefined,
    });

    options.statusHandler('');
    options.onSuccess(modal, normalized);
  } catch (error) {
    const message = error instanceof Error && error.message !== ''
      ? error.message
      : 'We couldn\'t send your link right now. Please try again.';
    options.statusHandler(message);
  } finally {
    options.triggerButton.disabled = false;
  }
}
